import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormArray, FormControl, FormBuilder, Validators, ValidatorFn } from '@angular/forms';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { MatSelectChange } from '@angular/material/select';
import { MatRadioChange } from '@angular/material/radio';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { SpinnerService } from '../../services/spinner.service';
import { ApiEntranceExitEntry, ApiSetEntranceExitEntriesRequest } from '../../api-client-module';
import { ApiEntranceExitQuestion } from '../../api-client-module';
import { EntranceExitEvent } from '../../firestore-models/entrance-exit-event';
import { EntranceExitQuestion } from '../../firestore-models/entrance-exit-question';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';

import { EntranceExitEntryCheckService, isAnyChecked } from './entrance-exit-entry-check.service';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface EntranceExitEntryDialogData {
  change_type: number;
  entry?: ApiEntranceExitEntry;
  event?: EntranceExitEvent | null;
}

@Component({
  selector: 'app-entrance-exit-entry-dialog',
  templateUrl: './entrance-exit-entry-dialog.component.html',
  styleUrls: ['./entrance-exit-entry-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class EntranceExitEntryDialogComponent implements OnInit {

  //入退場管理エントリー情報
  entry?: ApiEntranceExitEntry;

  applyForm: FormGroup;
  get options(): FormArray {
    return this.applyForm.get('options') as FormArray;
  }
  questions: Array<any> = [];

  projectId: string;

  projectLanguages!: Array<ProjectLanguage>;

  //入退場管理エントリー情報フォーム
  entryFormGroup!: FormGroup;
  emailAddressControl!: FormControl;
  companyNameControl!: FormControl;
  departmentControl!: FormControl;
  jobTitleControl!: FormControl;
  surnameChineseControl!: FormControl;
  nameChineseControl!: FormControl;
  familyNameControl!: FormControl;
  firstNameControl!: FormControl;
  birthdayControl!: FormControl;
  genderControl!: FormControl;
  postalCodeControl!: FormControl;
  prefectureControl!: FormControl;
  municipalitiesControl!: FormControl;
  streetAddressControl!: FormControl;
  nameAndNumberBuildingControl!: FormControl;
  mobilePhoneNumberControl!: FormControl;

  inEntranceExitEventId = '';

  isVisibledAddress = true;

  reservationStatuses = AppConst.RESERVATION_STATUSES;
  mailSendStatuses = AppConst.MAIL_SEND_STATUSES;
  genders = [''];

  private eventsCollection!: AngularFirestoreCollection<EntranceExitEvent>;
  eventsObservable!: Observable<EntranceExitEvent[]>;
  eventsSubscribe!: Subscription;
  events!: Array<EntranceExitEvent>;
  event!: EntranceExitEvent;

  private langChangeSub!: Subscription; // TranslateServiceの購読を管理 //(0703)
  currentLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<EntranceExitEntryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EntranceExitEntryDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private checkService: EntranceExitEntryCheckService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
    this.applyForm = this.formBuilder.group({
      options: this.formBuilder.array([]),
    }, {
      validator: []
    });
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.genders = [ ...this.genders, ...AppConst.GENDERS ];
    this.eventsCollection = this.angularFirestore.collection<EntranceExitEvent>(
      AppConst.FIRESTORE_ENTRANCE_EXIT_EVENTS, ref => ref.where('project_id', '==', this.projectId));
    this.eventsObservable = this.eventsCollection.valueChanges({ idField: 'id' });
    this.eventsSubscribe = this.eventsObservable.subscribe(events => {
      this.events = events;
    });    
    this.entry = this.data.entry;
    if (this.data.event) {
      this.event = this.data.event;
      this.questions = this.data.event.questions;
    }
    if (!this.entry) {
      this.entry = {
        project_id: this.projectId,
        facility_id: '',
        entrance_exit_event_id: '',
        uid: '',
        lang: '',
        entry_no: '',
        email_address: '',
        company_name: '',
        department: '',
        job_title: '',
        surname_chinese: '',
        name_chinese: '',
        family_name: '',
        first_name: '',
        birthday: null as any,
        gender: '',
        postal_code: '',
        prefecture: '',
        municipalities: '',
        street_address: '',
        name_and_number_building: '',
        mobile_phone_number: '',
        mail_send_status: AppConst.MAIL_SEND_STATUS_UNSEND,
        answer: [],
        preview: true,
        user_site_url: '',
        enabled: true,
        created: null as any,
        updated: null as any,
      };
    }

    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });

    // カスタム項目の設定がある場合はカスタム項目用のフォームを生成
    if (this.event.questions 
     && this.event.questions.length > 0
    ) {
      this.questions = this.event.questions;
      this.addOptionForm(this.questions);
    }
    this.createForm();
    this.emailAddressControl = this.entryFormGroup.get('emailAddress') as FormControl;
    this.companyNameControl = this.entryFormGroup.get('companyName') as FormControl;
    this.departmentControl = this.entryFormGroup.get('department') as FormControl;
    this.jobTitleControl = this.entryFormGroup.get('jobTitle') as FormControl;
    this.surnameChineseControl = this.entryFormGroup.get('surnameChinese') as FormControl;
    this.nameChineseControl = this.entryFormGroup.get('nameChinese') as FormControl;
    this.familyNameControl = this.entryFormGroup.get('familyName') as FormControl;
    this.firstNameControl = this.entryFormGroup.get('firstName') as FormControl;
    this.birthdayControl = this.entryFormGroup.get('birthday') as FormControl;
    this.genderControl = this.entryFormGroup.get('gender') as FormControl;
    this.postalCodeControl = this.entryFormGroup.get('postalCode') as FormControl;
    this.prefectureControl = this.entryFormGroup.get('prefecture') as FormControl;
    this.municipalitiesControl = this.entryFormGroup.get('municipalities') as FormControl;
    this.streetAddressControl = this.entryFormGroup.get('streetAddress') as FormControl;
    this.nameAndNumberBuildingControl = this.entryFormGroup.get('nameAndNumberBuilding') as FormControl;
    this.mobilePhoneNumberControl = this.entryFormGroup.get('mobilePhoneNumber') as FormControl;
  }


  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }
  
  //以下、フォームの記入エラー表示
  public getErrorMessageToEmailAddress() {
    return this.emailAddressControl.hasError('required')
    ? this.translate.instant('必須項目')
    : this.emailAddressControl.hasError('pattern')
    ? this.translate.instant('メールアドレス形式で入力')
    : '';
  }

  public getErrorMessageToFamilyName() {
    return this.familyNameControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToFirstName() {
    return this.firstNameControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToGender() {
    return this.genderControl.hasError('required')
    ? this.translate.instant('必須項目')
    : '';
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.entryFormGroup = this.formBuilder.group(
      {
        emailAddress: [this.entry!.email_address, [Validators.required, Validators.pattern('^[a-zA-Z0-9_+-]+(.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$')]],
        companyName: [this.entry!.company_name, []],
        department: [this.entry!.department, []],
        jobTitle: [this.entry!.job_title, []],
        surnameChinese: [this.entry!.surname_chinese, []],
        nameChinese: [this.entry!.name_chinese, []],
        familyName: [this.entry!.family_name, []],
        firstName: [this.entry!.first_name, []],
        birthday: [this.entry!.birthday, []],
        gender: [this.entry!.gender ? this.entry!.gender : '', []],
        postalCode: [this.entry!.postal_code, []],
        prefecture: [this.entry!.prefecture, []],
        municipalities: [this.entry!.municipalities, []],
        streetAddress: [this.entry!.street_address, []],
        nameAndNumberBuilding: [this.entry!.name_and_number_building, []],
        mobilePhoneNumber: [this.entry!.mobile_phone_number, []],
      }, {
        validator: []
      }
  );
  }

  /**
   * 入力項目の作成
   */
  addOptionForm(questions:EntranceExitQuestion[]) {
    if (this.entry) {
      //  設問分ループ
      for (let i = 0 ; i < questions.length ; i++) {
        // バリデーション設定
        const validators: ValidatorFn[] = this.checkService.createValidatorList(
          questions[i].question_type!,
          questions[i].question_check!,
          questions[i].question_check_range_lower!,
          questions[i].question_check_range_upper!,
          questions[i].question_check_col_count_lower!,
          questions[i].question_check_col_count_upper!,
        );
        // フォーム項目設定
        if (questions[i].question_type == 'string') {
          let defaultValue = '';
          if (this.entry.answer) {
            for (const answerValue of this.entry.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.formBuilder.group({name: questions[i].question_names[0].name, value: new FormControl(defaultValue, validators)}));

        } else if (questions[i].question_type == 'text-area') {
          let defaultValue = '';
          if (this.entry.answer) {
            for (const answerValue of this.entry.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.formBuilder.group({name: questions[i].question_names[0].name, value: new FormControl(defaultValue, validators)}));

        } else if (questions[i].question_type == 'number') {
          let defaultValue = '';
          if (this.entry.answer) {
            for (const answerValue of this.entry.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.formBuilder.group({name: questions[i].question_names[0].name, value: new FormControl(defaultValue, validators)}));

        } else if (questions[i].question_type == 'select') {
          let defaultValue = '';
          if (this.entry.answer) {
            for (const answerValue of this.entry.answer) {
              if (questions[i].question_id === answerValue.id) {
                defaultValue = answerValue.value;
              }
            }
          }
          this.options.push(this.createSelectItemGroup(questions[i], defaultValue, validators));

        } else if (questions[i].question_type == 'checkbox') {
          const isRequired = questions[i].question_check?.some(valid => valid === AppConst.QUESTION_ITEM_CHECK_REQUIRED) ?? false; // 必須確認
          const checkItems: FormGroup[] = [];
          for (let opt of questions[i].question_select_items!) {
            let isSelected = false;
            if (this.entry.answer) {
              for (const answerValue of this.entry.answer) {
                if (questions[i].question_id === answerValue.id) {
                  for (const answer of answerValue.value) {
                    if (opt.value == answer) {
                      isSelected = true;
                    }
                  }
                }
              }
            }
            checkItems.push(this.formBuilder.group({name: opt.items[0].item, value: opt.value, selected: isSelected}));
          }
          this.options.push(this.createCheckItemGroup(questions[i], checkItems, isRequired));

        } else if (questions[i].question_type == 'radio') {
          const radioItems: FormGroup[] = [];
          for (let opt of questions[i].question_select_items!) {
            let isSelected = false;
            if (this.entry.answer) {
              for (const answerValue of this.entry.answer) {
                if (questions[i].question_id === answerValue.id) {
                  if (opt.value == answerValue.value) {
                    isSelected = true;
                  }
                }
              }
            }
            radioItems.push(this.formBuilder.group({name: opt.items[0].item, value: new FormControl(opt.value, validators), selected: isSelected}));
          }
          this.options.push(this.createRadioItemGroup(questions[i], radioItems));
        } else {
        }
      }
    }
  }

  /**
   * プルダウン項目に関連項目を含めてFormbuilderに追加する
   */
  private createSelectItemGroup(question: EntranceExitQuestion, answer: string, validators: ValidatorFn[]) {
    let itemGroup = this.formBuilder.group({});
    itemGroup.addControl('name', new FormControl(question.question_names[0].name));
    itemGroup.addControl('value', new FormControl(answer, validators));
    return itemGroup;
  }

  /**
   * チェックボックスを関連項目を含めてFormbuilderに追加する
   */
  private createCheckItemGroup(question: EntranceExitQuestion, checkGroup: FormGroup<any>[], isRequired: boolean) {
    let itemGroup = this.formBuilder.group({}, {validators: isRequired ? isAnyChecked() : []});
    itemGroup.addControl('name', new FormControl(question.question_names[0].name));
    itemGroup.addControl('value', new FormControl(checkGroup));
    return itemGroup;
  }

  /**
   * ラジオボタンを関連項目を含めてFormbuilderに追加する
   */
  private createRadioItemGroup(question: EntranceExitQuestion, radioGroup: FormGroup<any>[]) {
    let itemGroup = this.formBuilder.group({});
    itemGroup.addControl('name', new FormControl(question.question_names[0].name));
    itemGroup.addControl('value', new FormControl(radioGroup));
    return itemGroup;
  }

  /**
   * SelectのChangeイベント
   */
  selectChange(event: MatSelectChange, itemPosition: number) {
  }

  /**
   * CheckBoxのChangeイベント
   */
  checkToggle(event: MatCheckboxChange, itemPosition: number, checkPosition: number) {
    this.options.value[itemPosition].value[checkPosition].value.selected = event.checked;
  }

  /**
   * RadioのChangeイベント
   */
  radioChange(event: MatRadioChange, itemPosition: number) {
    for (let opt of this.options.value[itemPosition].value) {
      if (opt.value.value == event.source.value) {
        opt.value.selected = true;
      } else {
        opt.value.selected = false;
      }
    }
  }

  /**
   * 必須のラベル表示判定
   */
  isRequired(checks: string[]): boolean {
    if (checks.includes(AppConst.QUESTION_ITEM_CHECK_REQUIRED)) {
      return true;
    }
    return false;
  }

  //更新ボタン
  addEntry(): void {
    if (this.entryFormGroup.valid) {
      this.entry!.email_address = this.emailAddressControl.value;
      this.entry!.company_name = this.companyNameControl.value;
      this.entry!.department = this.departmentControl.value;
      this.entry!.job_title = this.jobTitleControl.value;
      this.entry!.surname_chinese = this.surnameChineseControl.value;
      this.entry!.name_chinese = this.nameChineseControl.value;
      this.entry!.family_name = this.familyNameControl.value;
      this.entry!.first_name = this.firstNameControl.value;
      this.entry!.birthday = this.birthdayControl.value;
      this.entry!.gender = this.genderControl.value;
      this.entry!.postal_code = this.postalCodeControl.value;
      this.entry!.prefecture = this.prefectureControl.value;
      this.entry!.municipalities = this.municipalitiesControl.value;
      this.entry!.street_address = this.streetAddressControl.value;
      this.entry!.name_and_number_building = this.nameAndNumberBuildingControl.value;
      this.entry!.mobile_phone_number = this.mobilePhoneNumberControl.value;
      let answer = [];
      // カスタム項目がある場合
      if (this.questions && this.questions.length > 0) {
        for (let i=0; i<this.questions.length; i++) {
          if (this.questions[i].question_type == 'checkbox') {
            let checkboxValues = [];
            for (const value of this.options.value[i].value) {
              if (value.value.selected) {
                checkboxValues.push(value.value.value);
              }
            }
            answer.push({id:this.questions[i].question_id,value:checkboxValues});
          } else if (this.questions[i].question_type == 'radio') {
            for (const value of this.options.value[i].value) {
              if (value.value.selected) {
                answer.push({id:this.questions[i].question_id,value:value.value.value});
              }
            }
          } else {
            answer.push({id:this.questions[i].question_id,value:this.options.value[i].value});
          }
        }
      }
      this.entry!.answer = answer;
      if (this.entry) {
        let request: ApiSetEntranceExitEntriesRequest = {
          access_token: '',
          change_type: this.data.change_type,
          entries: [this.entry]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setEntranceExitEntries(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  clearBirthday(): void {
    this.birthdayControl.setValue(null);
  }

}
